import { ArrowIcon } from "@/assets/images/svgIcons/arrowIcon"
import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, appToast as toast, warningToastClassName } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useBoxOffers } from "@/modules/boxOffer/hooks/useBoxOffer"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useBoxNavigate } from "@/modules/navigation/hooks/useBoxNavigate"
import { useMySpaceNavigate } from "@/modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@/modules/navigation/routes"
import { usePrograms } from "@/modules/programs/hooks/usePrograms"
import { ProgramFilter } from "@/modules/programs/types/programTypes"
import { TagsIcon } from "lucide-react"

export function BoxPromotionCodeCard() {
  const boxNavigate = useBoxNavigate()
  const canEditFinancial = useBoxStaffPermission(BoxStaffPermissions.EDIT_FINANCIAL)
  const { data: offers } = useBoxOffers({ onlyWithPrice: true })

  const handleViewPromocodeClick = () => {
    if (!canEditFinancial) return
    if (offers.length > 0) boxNavigate(`${Path.Box.Finances.Root}/${Path.Box.Finances.PromoCodes}`)
    else
      toast.warning(word("promoCode.toast.missingOffer"), {
        classNames: warningToastClassName(),
      })
  }

  return <PromotionCodeCardComponent onViewPromocodeClick={handleViewPromocodeClick} />
}

export function UserPromotionCodeCard() {
  const mySpaceNavigate = useMySpaceNavigate()
  const { data: programs } = usePrograms({ programFilter: ProgramFilter.OWNED, onlyWithPrice: true, size: 1 })

  const handleViewPromocodeClick = () => {
    if (programs.content.length > 0) {
      mySpaceNavigate(`${Path.MySpace.Finances.Root}/${Path.MySpace.Finances.PromoCodes}`)
    } else {
      toast.error(word("promoCode.toast.missingProgram"), {
        classNames: errorToastClassName(),
      })
    }
  }

  return <PromotionCodeCardComponent onViewPromocodeClick={handleViewPromocodeClick} />
}

type PromotionCodeCardComponentProps = {
  onViewPromocodeClick: () => void
} & React.ComponentProps<"div">

function PromotionCodeCardComponent({ className, onViewPromocodeClick, ...props }: PromotionCodeCardComponentProps) {
  return (
    <div
      className={cn(
        "flex max-w-54 flex-col justify-between overflow-hidden rounded-4xl bg-surface-background-secondary px-3 py-2 md:gap-6 md:px-8 md:py-6",
        className,
      )}
      {...props}
    >
      <div className="flex flex-col items-start gap-1">
        <TagsIcon color="currentColor" className="size-6" />
        <Text fontStyle="TL">{word("finances.page.promoCodes.title")}</Text>
      </div>

      <Button variant="secondary" size="iconMd" onClick={onViewPromocodeClick} className="self-end">
        <ArrowIcon arrowDirection="right" color="currentColor" className="size-4" />
      </Button>
    </div>
  )
}
